import React from 'react';
import tw from 'tailwind.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';

import { styled, css } from 'src/theme';

const Label = styled.label<{ disabled?: boolean }>`
	${({ disabled }) =>
		disabled
			? tw`text-gray`
			: css`
					${tw`
				select-none
				cursor-pointer
			`}

					&:hover {
						${tw`text-blue-light`}
					}
			  `}
`;

const Input = styled.input.attrs({ type: 'radio' })`
	${tw`appearance-none`}
`;

const FakeRadio = styled.span`
	transition: 150ms;
	${tw`
		inline-block
		w-4
		h-4
		border
		border-solid
		border-gray
		rounded-full
		inline-flex
		flex-column
		items-center
		content-center
		justify-center
		text-transparent
		text-base
		align-middle
	`}

	label:hover ${Input}:enabled + & {
		${tw`border-blue-light`}
	}

	${Input}:enabled:checked + & {
		${tw`text-black`}
	}

	${Input}:disabled:checked + & {
		${tw`text-gray`}
	}
`;

const LabelText = styled.span`
	transition: 150ms;
	${tw`
		ml-2
		align-middle
	`}
`;

type RadioProps = Omit<React.ComponentPropsWithRef<'input'>, 'type'> & {
	label?: React.ReactNode;
};

/**
 * A styled checkbox with an optional `label`. The `label`, `style`, and
 * `className` are all used by the wrapper, while the remainder of the props are
 * passed through to the HTML checkbox.
 */
export const Radio = ({
	label,
	style,
	className,
	...checkboxProps
}: RadioProps): JSX.Element => {
	const { disabled } = checkboxProps;
	return (
		<Label style={style} className={className} disabled={disabled}>
			<Input {...checkboxProps} />
			<FakeRadio>
				<FontAwesomeIcon icon={faCircle} transform="shrink-5.5" />
			</FakeRadio>
			{label && <LabelText>{label}</LabelText>}
		</Label>
	);
};
